import { Box, Button, Card, Divider, Tooltip, Typography, useTheme } from '@mui/material';
import { Checkmark } from '@brainpool/icon-pack';
import { useCreateStripeCheckoutSession } from '../../hooks/useCreateStripeCheckoutSession';
import { useCreateStripePortalSession } from '../../hooks/useCreateStripePortalSession';
import { useOrganizationBillingInfo } from '../../hooks/useOrganizationBillingInfo';

export interface BillingCardProps {
  title: string;
  projects: string;
  reports: string;
  pricePerCredit: number;
  customTemplate: number;
  numOfConsultations: number;
  price: number;
}

export function BillingCard({
  title,
  projects,
  reports,
  pricePerCredit,
  customTemplate,
  numOfConsultations,
  price,
}: BillingCardProps) {
  const theme = useTheme();
  const mapTitleToProductId: Record<string, number> = {
    Standard: 0,
    Pro: 1,
    Elite: 2,
  };

  const {
    createCheckoutSession,
    data: checkoutData,
    loading: checkoutLoading,
  } = useCreateStripeCheckoutSession();
  const { orgBillingInfo, loading: billingLoading } = useOrganizationBillingInfo();
  const { createPortalSession, loading: portalLoading } = useCreateStripePortalSession();

  // Map numeric subscription level to plan title
  const getCurrentTierTitle = (subscriptionLevel: string | number) => {
    switch (subscriptionLevel) {
      case '0':
      case 0:
        return 'Standard';
      case '1':
      case 1:
        return 'Pro';
      case '2':
      case 2:
        return 'Elite';
      default:
        return 'Standard';
    }
  };

  const currentTierTitle = getCurrentTierTitle(orgBillingInfo?.subscription_level || '0');

  const onClickHandler = async () => {
    if (currentTierTitle === title) {
      return;
    }

    if (currentTierTitle === 'Standard') {
      await createCheckoutSession(mapTitleToProductId[title]);
    } else {
      const { data } = await createPortalSession();
      if (data?.createStripePortalSession?.url) {
        window.location.href = data.createStripePortalSession.url;
      }
    }
  };

  const getButtonLabel = (currentPlan: string, planTitle: string) => {
    if (currentPlan === planTitle) return 'Current plan';

    if (currentPlan === 'Standard') {
      return `Upgrade to ${planTitle}`;
    }

    if (currentPlan === 'Pro') {
      if (planTitle === 'Standard') return 'Switch to Standard';
      if (planTitle === 'Elite') return 'Upgrade to Elite';
    }

    if (currentPlan === 'Elite') {
      if (planTitle === 'Standard') return 'Switch to Standard';
      if (planTitle === 'Pro') return 'Switch to Pro';
    }

    return '';
  };

  return (
    <Card
      sx={{
        p: 3,
        backgroundColor: theme.palette.background.paper,
        height: '100%',
        gap: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="h3" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h4" sx={{ mt: 1 }} fontWeight={600} color="warning.contrastText">
        {price > 0
          ? `${price.toLocaleString('en-GB', {
              style: 'currency',
              currency: 'GBP',
              maximumFractionDigits: 0,
            })} / month`
          : 'Free'}
      </Typography>
      <Tooltip title={currentTierTitle === title ? 'You are already on this plan' : ''}>
        <Button
          variant={currentTierTitle === title ? 'outlined' : 'contained'}
          color="primary"
          sx={{ mt: 1 }}
          onClick={onClickHandler}
          fullWidth
          disabled={
            billingLoading || checkoutLoading || portalLoading || currentTierTitle === title
          }
        >
          {checkoutLoading ? 'Creating checkout...' : getButtonLabel(currentTierTitle, title)}
        </Button>
      </Tooltip>
      <Divider sx={{ my: 2 }} />
      <Box display="flex" alignItems="start" gap={1}>
        <Checkmark fill={theme.palette.success.main} size={16} />
        <Typography variant="body1" sx={{ maxWidth: 'calc(100% - 32px)' }}>
          {projects}
        </Typography>
      </Box>
      <Box display="flex" alignItems="start" gap={1}>
        <Checkmark fill={theme.palette.success.main} size={16} />
        <Typography variant="body1" sx={{ maxWidth: 'calc(100% - 32px)' }}>
          {reports}
        </Typography>
      </Box>
      <Box display="flex" alignItems="start" gap={1}>
        <Checkmark fill={theme.palette.success.main} size={16} />
        <Typography variant="body1" sx={{ maxWidth: 'calc(100% - 32px)' }}>
          Additional Due Diligence credits can be purchased at{' '}
          {pricePerCredit.toLocaleString('en-GB', {
            style: 'currency',
            currency: 'GBP',
            maximumFractionDigits: 0,
          })}
        </Typography>
      </Box>
      {customTemplate ? (
        <Box display="flex" alignItems="start" gap={1}>
          <Checkmark fill={theme.palette.success.main} size={16} />
          <Typography variant="body1" sx={{ maxWidth: 'calc(100% - 32px)' }}>
            {`${customTemplate} Custom reporting template${customTemplate > 1 ? 's' : ''}`}
          </Typography>
        </Box>
      ) : null}
      {numOfConsultations ? (
        <Box display="flex" alignItems="start" gap={1}>
          <Checkmark fill={theme.palette.success.main} size={16} />
          <Typography variant="body1" sx={{ maxWidth: 'calc(100% - 32px)' }}>
            {numOfConsultations} Consultations with PhD-level AI experts
          </Typography>
        </Box>
      ) : null}
    </Card>
  );
}
